import {textToHtml} from '@frontapp/editor-html';
import React, {FC, useCallback, useState} from 'react';

import {
  autoGenerateDraft,
  DraftAssistantLanguageOptionsEnum,
  DraftAssistantToneOptionsEnum,
} from '../../../../../libs/shared/core/src/helpers/ai/composeHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ReplyFactModel} from '../../../../../libs/shared/core/src/models/api/factModel';
import {MessageModel} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {RecipientRolesEnum} from '../../../../../libs/shared/core/src/models/api/recipientModel';
import {useAppQuery} from '../../../../../libs/shared/core/src/services/query/useAppQuery';
import {ReplyLocationsEnum} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {
  EmlPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useAppDispatch} from '../../../../helpers/appReduxHelpers';
import {ReplyTypesEnum} from '../../../../helpers/models/recipientHelpers';
import {TagModel} from '../../../../models/api/tagModel';
import {tagQueries} from '../../../../queries/tagQueries';
import {ThunkAsyncAction} from '../../../../store/appThunk';
import {getCompanyUrl} from '../../../../store/data/company/companySelectors';
import {addTagToConversationsAction} from '../../../../store/data/conversations/tagConversationsActions';
import {
  ExistingDraftStrategiesEnum,
  replyToMessageAction,
} from '../../../../store/data/messages/draftMessagesActions';
import {getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {AiReplyPreview} from './aiReplyPreview';
import {InheritableTriggerButtonProps, TriggerButton} from './triggerButton';

interface AiReplyButtonProps extends InheritableTriggerButtonProps {
  aiReply: ReplyFactModel;
  message: MessageModel;
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId;
}

export const AiReplyButton: FC<AiReplyButtonProps> = ({aiReply, message, renderDropdown, panelId}) => {
  const [isRunning, setIsRunning] = useState(false);
  const dispatch = useAppDispatch();

  const {data: trackTag} = useAppQuery(tagQueries.id(aiReply.trackTagId));

  const title = isRunning ? (
    <Trans id="workflow-actions-generating-reply">Generating draft</Trans>
  ) : (
    <Trans id="workflow-actions-suggest-reply">Suggest reply</Trans>
  );

  const onAcceptAiReply = useCallback(async () => {
    setIsRunning(true);

    try {
      await dispatch(acceptAiReply(aiReply, trackTag, message, panelId));
    } catch (err) {
      // TODO: error banner
    } finally {
      setIsRunning(false);
    }
  }, [dispatch, aiReply, trackTag, message, panelId]);

  return (
    <TriggerButton
      title={title}
      isLoading={isRunning}
      onSelect={onAcceptAiReply}
      renderDropdown={renderDropdown}
      renderPreview={(anchor, popperPlacement) => (
        <AiReplyPreview anchor={anchor} popperPlacement={popperPlacement} aiReply={aiReply} />
      )}
    />
  );
};

function acceptAiReply(
  aiReply: ReplyFactModel,
  trackTag: TagModel | undefined,
  message: MessageModel,
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);
    const user = getUser(state);

    const fromRecipient = message.recipients.find((recipient) => recipient.role === RecipientRolesEnum.FROM);

    const draftText = await autoGenerateDraft({
      companyUrl,
      conversationId: message.conversationId,
      tone: DraftAssistantToneOptionsEnum.PROFESSIONAL,
      language: DraftAssistantLanguageOptionsEnum.MATCH_LANGUAGE,
      recipients: fromRecipient ? [fromRecipient] : [],
      draftText: aiReply.content,
      senderFirstName: user.givenName,
    });

    if (trackTag) {
      dispatch(addTagToConversationsAction([message.conversationId], trackTag.id, [trackTag]));
    }

    dispatch(
      replyToMessageAction({
        panelId,
        replyType: ReplyTypesEnum.ALL,
        replyToUid: message.uid,
        conversationId: message.conversationId,
        location: ReplyLocationsEnum.AI_SUGGESTION,
        existingDraftStrategy: ExistingDraftStrategiesEnum.THROW_ERROR,
        overrides: {
          text: draftText,
          trustedHtml: textToHtml(draftText),
        },
      }),
    );
  };
}
